import { Image, Palette, Upload } from "lucide-react";
import { useToast } from "@/components/shared";
import { companyProfileService } from "@/lib/services";
import { ColorField } from "./ColorField";
import { inputClass, labelClass, textareaClass } from "./styles";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The flat brand-kit fields the branding section reads/writes on the form. */
export interface BrandFields {
  brand_primary: string;
  brand_secondary: string;
  brand_accents: string;
  brand_background: string;
  brand_text: string;
  brand_font_heading: string;
  brand_font_body: string;
  brand_tone: string;
  brand_style: string;
}

type BrandFieldSetter = <K extends keyof BrandFields>(key: K, value: BrandFields[K]) => void;

// ---------------------------------------------------------------------------
// Logo upload
// ---------------------------------------------------------------------------

function LogoUpload({
  logoBlobUrl,
  onUploadComplete,
}: Readonly<{
  logoBlobUrl: string | null;
  onUploadComplete: () => Promise<void>;
}>) {
  const { showToast } = useToast();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await companyProfileService.uploadLogo(file);
      if (res.data?.logo_url) {
        await onUploadComplete();
        showToast("Logo uploaded", "success");
      }
    } catch {
      showToast("Failed to upload logo", "error");
    }
  };

  return (
    <div>
      <label className={labelClass}>
        <Image className="inline h-3.5 w-3.5 mr-1" />
        Company Logo
      </label>
      <div className="flex items-center gap-4">
        {logoBlobUrl && (
          <img
            src={logoBlobUrl}
            alt="Company logo"
            className="h-12 w-auto max-w-[200px] object-contain rounded border border-border-secondary p-1"
          />
        )}
        <label className="flex items-center gap-2 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg text-sm font-medium cursor-pointer transition-colors border border-border-secondary">
          <Upload className="h-4 w-4" />
          {logoBlobUrl ? "Replace Logo" : "Upload Logo"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            onChange={handleFileChange}
          />
        </label>
      </div>
      <p className="mt-1.5 text-xs text-content-tertiary">
        PNG, JPEG, SVG, or WebP. Max 5 MB. Used in generated output and branded exports.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

function BrandPaletteFields({ fields, setField }: Readonly<{ fields: BrandFields; setField: BrandFieldSetter }>) {
  return (
    <div>
      <h4 className="text-sm font-medium text-content-secondary mb-2">Brand Palette</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ColorField
          id="brand-primary"
          label="Primary"
          value={fields.brand_primary}
          onChange={(v) => setField("brand_primary", v)}
        />
        <ColorField
          id="brand-secondary"
          label="Secondary"
          value={fields.brand_secondary}
          onChange={(v) => setField("brand_secondary", v)}
        />
        <ColorField
          id="brand-background"
          label="Background"
          value={fields.brand_background}
          onChange={(v) => setField("brand_background", v)}
        />
        <ColorField
          id="brand-text"
          label="Text"
          value={fields.brand_text}
          onChange={(v) => setField("brand_text", v)}
        />
      </div>
      <div className="mt-3">
        <label htmlFor="brand-accents" className={labelClass}>
          Accent colors (comma-separated hex)
        </label>
        <input
          id="brand-accents"
          type="text"
          value={fields.brand_accents}
          onChange={(e) => setField("brand_accents", e.target.value)}
          placeholder="#00EDFF, #D13C8D"
          className={inputClass}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Typography + tone
// ---------------------------------------------------------------------------

function BrandTypographyFields({ fields, setField }: Readonly<{ fields: BrandFields; setField: BrandFieldSetter }>) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="brand-font-heading" className={labelClass}>
            Heading font
          </label>
          <input
            id="brand-font-heading"
            type="text"
            value={fields.brand_font_heading}
            onChange={(e) => setField("brand_font_heading", e.target.value)}
            placeholder="Poppins"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="brand-font-body" className={labelClass}>
            Body font
          </label>
          <input
            id="brand-font-body"
            type="text"
            value={fields.brand_font_body}
            onChange={(e) => setField("brand_font_body", e.target.value)}
            placeholder="Inter"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="brand-tone" className={labelClass}>
          Brand tone
        </label>
        <input
          id="brand-tone"
          type="text"
          value={fields.brand_tone}
          onChange={(e) => setField("brand_tone", e.target.value)}
          placeholder="e.g. formal, confident, approachable"
          className={inputClass}
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Supplemental prose
// ---------------------------------------------------------------------------

function BrandStyleField({ value, onChange }: Readonly<{ value: string; onChange: (value: string) => void }>) {
  return (
    <div>
      <label htmlFor="company-brand-style" className={labelClass}>
        <Palette className="inline h-3.5 w-3.5 mr-1" />
        Additional Brand Guidance (prose)
      </label>
      <textarea
        id="company-brand-style"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Use generous whitespace. Logo top-left on exports. Avoid drop shadows..."
        rows={3}
        maxLength={2000}
        className={textareaClass}
      />
      <p className="mt-1.5 text-xs text-content-tertiary">
        Supplemental free-text guidance. The structured palette and fonts above are authoritative; this adds anything
        they can't capture.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export function BrandingSection({
  fields,
  setField,
  logoBlobUrl,
  onLogoUploaded,
}: Readonly<{
  fields: BrandFields;
  setField: BrandFieldSetter;
  logoBlobUrl: string | null;
  onLogoUploaded: () => Promise<void>;
}>) {
  return (
    <div className="space-y-4">
      <LogoUpload logoBlobUrl={logoBlobUrl} onUploadComplete={onLogoUploaded} />
      <BrandPaletteFields fields={fields} setField={setField} />
      <BrandTypographyFields fields={fields} setField={setField} />
      <BrandStyleField value={fields.brand_style} onChange={(v) => setField("brand_style", v)} />
    </div>
  );
}
